import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import {
  tradeSuggestionsApi,
  useValidateSuggestionMutation,
} from 'store/services/tradeSuggestions/tradeSuggestionsApi';
import { TradeSuggestion } from 'types/interfaces/tradeSuggestion';
import { getShipsFirstMessage } from 'utils/fundingCopy';
import { buildSeedFromSuggestion, writeOfferBuilderSeed } from 'utils/offerBuilderSeed';
import {
  formatSuggestionUnavailableMessage,
  parseOfferError,
  SUGGESTION_GONE_MESSAGE,
} from 'utils/offerErrors';

interface Props {
  suggestion: TradeSuggestion;
  /** Locked out during the pass animation so a double click cannot skip one */
  disabled: boolean;
  onPass: () => void;
  ledgerOpen: boolean;
  onOpenLedger: () => void;
}

/**
 * The card footer: one row, not a stack of bars.
 *
 * Left is the funding outcome as a plain sentence with a check, deliberately
 * NOT a filled callout box — it is a statement, never a warning — followed by
 * the ledger disclosure as an inline dashed-underline link. Right is the action
 * pair with Pass to the LEFT of the dominant primary.
 *
 * "Passing frees these cards for other traders" is not persistent subtext at
 * desktop width; the undo toast carries that message when it is relevant.
 */
const SuggestionActions: React.FC<Props> = ({
  suggestion,
  disabled,
  onPass,
  ledgerOpen,
  onOpenLedger,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [validateSuggestion] = useValidateSuggestionMutation();
  const [isOpening, setIsOpening] = useState(false);
  const [staleMessage, setStaleMessage] = useState<string | null>(null);

  const handleOpen = async () => {
    setIsOpening(true);
    try {
      const result = await validateSuggestion(suggestion.id).unwrap();
      if (!result.valid) {
        setStaleMessage(
          result.details
            ? formatSuggestionUnavailableMessage(result.details)
            : SUGGESTION_GONE_MESSAGE
        );
        return;
      }

      // Null means a have row vanished between the passing validate and now.
      // buildSeedFromSuggestion logs the anomaly; the user gets the ordinary
      // stale state rather than a builder holding a partial bundle.
      const seed = buildSeedFromSuggestion(suggestion);
      if (!seed) {
        setStaleMessage(SUGGESTION_GONE_MESSAGE);
        return;
      }

      writeOfferBuilderSeed(seed);
      navigate(`/trade-offers/new/${suggestion.counterparty.username}`);
    } catch (err) {
      toast.error(parseOfferError(err).message);
    } finally {
      setIsOpening(false);
    }
  };

  const acknowledgeStale = () => {
    // The failed validate already released the rows server-side; refetch now
    // that the user has read which card went away.
    dispatch(tradeSuggestionsApi.util.invalidateTags(['Suggestions']));
  };

  if (staleMessage) {
    return (
      <div className="sugg-card-stale" role="alert">
        <p>{staleMessage}</p>
        <button type="button" className="sugg-card-stale-ack" onClick={acknowledgeStale}>
          Got it
        </button>
      </div>
    );
  }

  return (
    /* One wrapping row, NOT a left group plus a right group. The funding
       sentence, the ledger link and the button pair are siblings so that the
       row's flex rules apply to each of them directly: nested in a left group,
       `flex: 1 1 260px` on the sentence would have resolved against that inner
       container and the link would have slid under the buttons at ~1000px. */
    <div className="sugg-footer">
      <p className="sugg-footer-funding">
        {/* Both sides covered reads as a green check; a sequenced trade is a
            shipping fact, not a warning, so it takes a shipping glyph rather
            than an alert colour. */}
        {suggestion.shipsFirst === 'BOTH' ? (
          <span className="sugg-footer-check" aria-hidden="true">
            &#10003;
          </span>
        ) : (
          <span className="sugg-footer-ship" aria-hidden="true">
            &#128666;
          </span>
        )}
        {getShipsFirstMessage(suggestion.shipsFirst)}
      </p>
      {/* Hidden while open: the "Hide the full ledger" control lives at the
          card's top right, where the ledger replaced the axis. */}
      {!ledgerOpen && (
        <button
          type="button"
          className="sugg-footer-ledger-link"
          aria-expanded={false}
          onClick={onOpenLedger}
        >
          See every card, condition and price
        </button>
      )}

      <div className="sugg-footer-right">
        <button
          type="button"
          className="sugg-footer-pass"
          disabled={isOpening || disabled}
          onClick={onPass}
        >
          Pass
        </button>
        <button
          type="button"
          className="sugg-footer-cta"
          disabled={isOpening || disabled}
          onClick={handleOpen}
        >
          {isOpening ? 'Checking availability…' : 'Review and propose'}
        </button>
      </div>
    </div>
  );
};

export default SuggestionActions;
